/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/naming-convention */
import {
  ChevronLeft,
  FileText,
  ChevronDown,
  EllipsisVertical,
  PenLine,
  ReceiptText,
  Trash2,
  CircleFadingArrowUp,
  LoaderCircle,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router';
import parse from 'html-react-parser';
import ReactPDF from '@react-pdf/renderer';
import { format } from 'date-fns';
import { useEffect } from 'react';
import { getCoreRowModel, useReactTable, flexRender } from '@tanstack/react-table';
import LayoutAdmin from '../../../components/layouts/LayoutAdmin';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../../components/ui/breadcrumb';
import PaymentStatusBadge from '../../../components/ui/badge';

import { Button } from '../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import useIsMobile from '../../../hooks/use-mobile';
import InvoiceCanvas from './InvoiceCanvas';
import { columns } from './Column';
import { Table, TableBody, TableCell, TableRow, TableHead, TableHeader } from '../../../components/ui/table';
import useInvoice from '../../../stores/useInvoice';
import { ICompany, IContactPerson, IInvoice, IInvoiceProduct } from '../../../types/types';
import useAuth from '../../../stores/useAuth';
import useContact from '../../../stores/useContact';
import Skeleton from '../../../components/ui/skeleton';
import { formatDate } from '../../../utils/format';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../components/ui/dialog';
import { Card } from '../../../components/ui/card';
import { useNavigateBack } from '../../../hooks/useNavigateBack';

function ActionMenu({ contact_person }: { contact_person?: IContactPerson }) {
  const { invoice, loading, deleteInvoice } = useInvoice();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { company } = useAuth();

  return (
    <Dialog>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {!loading && (
            <Button type="button" variant={isMobile ? 'outline' : 'default'}>
              {!isMobile ? (
                <>
                  Action
                  <ChevronDown />
                </>
              ) : (
                <EllipsisVertical />
              )}
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56">
          <DropdownMenuGroup>
            <DropdownMenuItem onClick={() => navigate(`/invoice/edit/${invoice?.invoice_id}`)}>
              <PenLine />
              <span>Edit</span>
            </DropdownMenuItem>
            {invoice?.status_payment !== 'paid' && (
              <DropdownMenuItem
                onClick={() =>
                  navigate(`/receipt/create?partnerId=${invoice?.partner_id}&invoiceId=${invoice?.invoice_id}`)
                }
              >
                <ReceiptText />
                <span>Create receipt</span>
              </DropdownMenuItem>
            )}
            <DialogTrigger className="w-full">
              <DropdownMenuItem>
                <Trash2 />
                <span>Delete</span>
              </DropdownMenuItem>
            </DialogTrigger>
            <ReactPDF.PDFDownloadLink
              document={
                <InvoiceCanvas
                  invoice={invoice as IInvoice}
                  company={company as ICompany}
                  contact_person={contact_person}
                />
              }
              fileName={invoice?.invoice_number}
            >
              <DropdownMenuItem>
                <CircleFadingArrowUp />
                <span>Download PDF</span>
              </DropdownMenuItem>
            </ReactPDF.PDFDownloadLink>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      <DialogContent className="w-[calc(100dvw-32px)] md:w-full rounded-lg">
        <DialogHeader>
          <DialogTitle>Confirm delete invoice</DialogTitle>
          <DialogDescription className="py-4">
            are you sure you want to delete invoice <span className="font-semibold">{invoice?.invoice_number}</span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-row justify-end gap-2">
          <div>
            <Button variant="default" type="button" onClick={() => deleteInvoice(invoice?.invoice_id as string)}>
              Delete
            </Button>
          </div>
          <DialogClose>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ViewInvoicePage() {
  const { invoice, getInvoice, resetInvoice, loading } = useInvoice();
  const { invoiceId } = useParams();
  const { company } = useAuth();
  const { contacts, fetchContact } = useContact();

  const navigateBack = useNavigateBack('/invoice');

  const contact_person: IContactPerson | null = contacts?.find((c) => c.contact_id === invoice?.contact_id) || null;

  useEffect(() => {
    fetchContact();
  }, [fetchContact]);

  useEffect(() => {
    if (invoiceId) {
      getInvoice(invoiceId);
    }

    return () => {
      resetInvoice();
    };
  }, [invoiceId, getInvoice, resetInvoice]);

  const financialData = [
    { detail: 'Subtotal', amount: invoice?.subtotal },
    { detail: 'Total Discount', amount: 0 },
    { detail: 'Tax', amount: 0.0 },
    { detail: 'Total', amount: invoice?.amount_due },
  ];

  const table = useReactTable({
    data: (invoice?.invoice_products as IInvoiceProduct[]) || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const parsedDesc = parse(invoice?.description || '');

  return (
    <LayoutAdmin header="Invoice">
      <div className="flex gap-4 items-center">
        <button
          type="button"
          className="flex items-center gap-1 text-xs md:text-base cursor-pointer font-medium text-secondary"
          onClick={navigateBack}
        >
          <ChevronLeft size={16} />
          Back
        </button>
        <div className="w-2 h-2 rounded-full bg-slate-100" />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink>
                <Link to="/invoice" className="text-xs md:text-base">
                  Sales Invoice
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="text-xs md:text-base">View Sales Invoice</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <div className="flex justify-between items-center">
        <div className="flex flex-wrap gap-2 items-center">
          <FileText className="w-4 h-4 md:w-6 md:h-6" />
          {loading ? (
            <Skeleton className="w-40 h-6 rounded-md" />
          ) : (
            <p className="md:text-lg text-sm lg:text-xl font-medium">{invoice?.invoice_number}</p>
          )}
          <PaymentStatusBadge status={invoice?.status_payment as string} />
        </div>
        <ActionMenu contact_person={contact_person as IContactPerson} />
      </div>
      <div className="flex md:flex-row flex-col gap-4 mb-8">
        <Card className="p-4 flex-1">
          {loading ? (
            <div className="flex justify-center items-center h-full gap-2">
              <LoaderCircle className="animate-spin" />
              <p>Loading invoice data...</p>
            </div>
          ) : (
            <>
              {/* header invoice */}
              <div className="grid grid-cols-2">
                <img src={invoice?.logo} alt="Logo" className="rounded-lg w-16 h-16 lg:w-28 lg:h-28" />
                <div className="flex flex-col gap-2 flex-1">
                  <p className="text-blue-700 font-medium text-right text-[9px] lg:text-base">Invoice</p>
                  <div className="grid grid-cols-2 text-xs gap-2">
                    <div className="text-right">
                      <p className="font-semibold ml-auto text-[9px] lg:text-sm">Reference</p>
                      <p className="font-semibold ml-auto text-[9px] lg:text-sm">Date</p>
                      <p className="font-semibold ml-auto text-[9px] lg:text-sm">Due Date</p>
                      <p className="font-semibold ml-auto text-[9px] lg:text-sm">Tax Identification Number</p>
                    </div>
                    <div>
                      <p className="font-normal text-right text-[9px] lg:text-sm">{invoice?.invoice_number}</p>
                      <p className="font-normal text-right text-[9px] lg:text-sm">
                        {invoice?.date ? formatDate(invoice.date) : 'N/A'}
                      </p>
                      <p className="font-normal text-right text-[9px] lg:text-sm">
                        {invoice?.due_date ? formatDate(invoice.due_date) : 'N/A'}
                      </p>
                      <p className="font-normal text-right text-[9px] lg:text-sm">{company?.taxIdent || '-'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* information */}
              <div className="grid grid-cols-2 mt-8 gap-4 md:gap-8">
                <div className="flex flex-col gap-4">
                  <p className="text-[9px] lg:text-sm">Our Information</p>
                  <div className="bg-primary h-[1px] w-full" />
                  <p className="text-[9px] lg:text-sm text-text font-semibold">{company?.name}</p>
                  <div className="flex flex-col text-[9px] lg:text-xs">
                    <p>{company?.address_line_1}</p>
                    {company?.address_line_2 ? (
                      <p>
                        {company?.address_line_2} {company?.city},
                      </p>
                    ) : (
                      <p>{company?.city}</p>
                    )}
                    {company?.state ? (
                      <p>
                        {company?.state}, {company?.zip_code}
                      </p>
                    ) : (
                      <p>{company?.zip_code}</p>
                    )}
                    <p>{company?.country}</p>
                    <p>{company?.phone ? `Phone: ${company?.phone}` : ''}</p>
                    <p>{company?.email ? `Email: ${company?.email}` : ''}</p>
                  </div>
                </div>
                <div className="flex flex-col gap-4 max-w-sc">
                  <p className="text-[9px] lg:text-sm">Billing For</p>
                  <div className="bg-primary h-[1px] w-full" />
                  <p className="text-[9px] lg:text-sm text-text font-semibold">{invoice?.partner?.name}</p>
                  <div className="flex flex-col text-[9px] lg:text-xs">
                    <p>{contact_person?.name && contact_person?.name}</p>
                    <p>
                      {contact_person?.phone ? `Phone: ${contact_person?.phone}` : `Phone: ${invoice?.partner?.phone}`}
                    </p>
                    <p>
                      {contact_person?.email ? `Email: ${contact_person?.email}` : `Email: ${invoice?.partner?.email}`}
                    </p>
                  </div>
                </div>
              </div>

              {/* Table of Products */}
              <div className="mt-8">
                {invoice && (
                  <div className="rounded-md border w-[calc(100dvw-70px)] md:w-full">
                    <Table className="overflow-x-scroll">
                      <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                          <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                              <TableHead key={header.id} className="text-xs">
                                {header.isPlaceholder
                                  ? null
                                  : flexRender(header.column.columnDef.header, header.getContext())}
                              </TableHead>
                            ))}
                          </TableRow>
                        ))}
                      </TableHeader>
                      <TableBody>
                        {table.getRowModel().rows?.length ? (
                          table.getRowModel().rows.map((row) => (
                            <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                              {row.getVisibleCells().map((cell) => (
                                <TableCell key={cell.id} className="text-xs">
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={columns.length} className="h-24 text-center text-xs">
                              No results.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
              <div className="md:w-1/2 w-full my-8 text-[9px] lg:text-xs ml-auto">
                <Table>
                  <TableBody>
                    {financialData.map((item) => (
                      <TableRow key={item.detail}>
                        <TableCell className="text-[9px] lg:text-xs">{item.detail}</TableCell>
                        <TableCell className="text-right text-[9px] lg:text-xs">
                          {Intl.NumberFormat('id-ID', {
                            style: 'currency',
                            currency: 'IDR',
                          }).format(item.amount ?? 0)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Notes and Signature */}
              <div className="grid grid-cols-2 gap-4 text-[9px] lg:text-xs mb-4">
                <div className="flex flex-col gap-4">
                  <p className="text-[9px] lg:text-sm font-semibold">Notes</p>
                  <div className="bg-primary h-[1px] w-full" />
                  <p>{parsedDesc}</p>
                </div>
                <div className="flex flex-col gap-2 items-center pt-4 min-h-36">
                  <p className="font-semibold text-[9px] lg:text-sm">
                    {invoice?.date ? format(invoice.date, 'LLL dd, y') : 'N/A'}
                  </p>
                  {invoice?.signature ? (
                    <img src={invoice?.signature} alt="Signature" className="rounded-lg w-28 h-28" />
                  ) : (
                    <div className="w-28 h-28" />
                  )}
                  <p className="font-semibold text-[9px] lg:text-sm">{invoice?.signature_name}</p>
                </div>
              </div>
            </>
          )}
        </Card>
        <Card className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-2 h-max md:w-1/3 w-full">
          {loading ? (
            <>
              <Skeleton className="w-full h-6 rounded-md" />
              <Skeleton className="w-full h-6 rounded-md" />
              <Skeleton className="w-full h-6 rounded-md mt-2" />
              <Skeleton className="w-full h-6 rounded-md mt-2" />
              <Skeleton className="w-full h-6 rounded-md mt-2" />
              <Skeleton className="w-full h-6 rounded-md mt-2" />
            </>
          ) : (
            <>
              <div>
                <p className="text-sm md:text-base">Partner</p>
                <p className="text-sm md:text-base font-semibold">{invoice?.partner?.name}</p>
              </div>
              <div>
                <p className="text-sm md:text-base">Invoice Total</p>
                <p className="text-sm md:text-base font-semibold">
                  {Intl.NumberFormat('id-ID', {
                    style: 'currency',
                    currency: 'IDR',
                  }).format(invoice?.amount_due ?? 0)}
                </p>
              </div>
              <div>
                <p className="text-sm md:text-base">Invoice Date</p>
                <p className="text-sm md:text-base font-semibold">
                  {invoice?.date ? format(invoice.date, 'LLL dd, y') : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm md:text-base">Due Date</p>
                <p className="text-sm md:text-base font-semibold">
                  {invoice?.due_date ? format(invoice.due_date, 'LLL dd, y') : 'N/A'}
                </p>
              </div>
            </>
          )}
        </Card>
      </div>
    </LayoutAdmin>
  );
}
